/**
 * SettingsAuditAccessPage — 2026-02-13 (user request).
 * Menu Settings baru untuk kelola OVERRIDE cakupan Audit Log per user.
 * Gate: `audit_log_access.update` (BOD Lv1 bypass otomatis).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileClock, Save, X, Search, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatDetail } from "@/lib/api";

const DIVISIONS = ["BOD", "FAT", "HRGA", "Procurement Asset Logistic", "Project"];

export default function SettingsAuditAccessPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [canManage, setCanManage] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-log-access");
      setItems(data.items || []);
    } catch (err) {
      const msg = formatDetail(err?.response?.data?.detail) || "Gagal memuat data.";
      toast.error(msg);
      if (err?.response?.status === 403) setCanManage(false);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter((u) =>
      (u.name || "").toLowerCase().includes(query)
      || (u.email || "").toLowerCase().includes(query)
      || (u.division || "").toLowerCase().includes(query));
  }, [items, q]);

  if (!canManage) {
    return (
      <div className="p-6" data-testid="audit-access-forbidden">
        <div className="bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-800">
          Anda tidak berhak mengelola Akses Audit Log. Minta admin memberi perm "Ubah" pada "Akses Audit Log" di Access Control.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="audit-access-page">
      <button type="button" onClick={() => nav("/settings")}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]">
        <ArrowLeft size={11} /> Settings
      </button>
      <div>
        <p className="overline text-slate-500">Compliance · Access Control</p>
        <h1 className="font-display text-3xl tracking-tight text-[#0B1D3A]">Akses Audit Log</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Setiap user secara default hanya bisa melihat log dirinya sendiri + level yang lebih rendah di divisinya (hierarki).
          Di sini Anda bisa memberi <b>override</b>: cakupan "Semua user", divisi tertentu, atau daftar user pilihan.
          BOD Lv1 selalu melihat log semua user.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative w-full sm:w-80">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama / email / divisi…" data-testid="audit-access-search"
            className="w-full h-9 border border-slate-300 pl-8 pr-2 text-[11px] bg-white focus:outline-none focus:border-[#0B1D3A]" />
        </div>
      </div>

      <div className="border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="audit-access-table">
          <thead className="bg-slate-50 border-b border-slate-200 text-[9px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">User</th>
              <th className="px-3 py-2 text-left">Divisi · Lv</th>
              <th className="px-3 py-2 text-left">Cakupan Efektif</th>
              <th className="px-3 py-2 text-left">Override</th>
              <th className="px-3 py-2 text-right w-40">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="text-center py-8 text-slate-400 italic">Memuat…</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-slate-400 italic">Tidak ada user.</td></tr>
              : filtered.map((u) => {
                const g = u.grant;
                return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-access-row-${u.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-[#0B1D3A]">{u.name}</div>
                      <div className="text-[10px] text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{u.division} · Lv{u.level}</td>
                    <td className="px-3 py-2 text-slate-600">{u.effective_summary}</td>
                    <td className="px-3 py-2">
                      {!g ? <span className="text-slate-400 italic">—</span> : (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-semibold bg-violet-50 text-violet-700 border border-violet-300">
                          {g.scope_type === "all" ? "Semua User" : g.scope_type === "division" ? `Divisi (${(g.target_divisions || []).length})` : `User (${(g.target_user_ids || []).length})`}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" onClick={() => setEditing({ user: u, ...(g || { scope_type: "all", target_divisions: [], target_user_ids: [] }) })}
                        data-testid={`audit-access-edit-${u.id}`}
                        className="px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold">Kelola Cakupan</button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {editing && <ScopeDialog data={editing} allUsers={items} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ScopeDialog({ data, allUsers, onClose, onSaved }) {
  const [scopeType, setScopeType] = useState(data.scope_type || "all");
  const [divisions, setDivisions] = useState(data.target_divisions || []);
  const [userIds, setUserIds] = useState(data.target_user_ids || []);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const targetUser = data.user;

  const toggleDiv = (d) => setDivisions((x) => x.includes(d) ? x.filter((v) => v !== d) : [...x, d]);
  const toggleUser = (id) => setUserIds((x) => x.includes(id) ? x.filter((v) => v !== id) : [...x, id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/audit-log-access/${targetUser.id}`, {
        scope_type: scopeType,
        target_divisions: scopeType === "division" ? divisions : [],
        target_user_ids: scopeType === "user" ? userIds : [],
      });
      toast.success("Cakupan tersimpan.");
      onSaved();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail) || "Gagal menyimpan."); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm(`Cabut override untuk ${targetUser.name}? User kembali ke aturan hierarki default.`)) return;
    setBusy(true);
    try {
      await api.delete(`/audit-log-access/${targetUser.id}`);
      toast.success("Override dicabut.");
      onSaved();
    } catch (err) { toast.error(formatDetail(err?.response?.data?.detail) || "Gagal mencabut."); }
    finally { setBusy(false); }
  };

  const filteredUsers = useMemo(() => {
    const query = q.trim().toLowerCase();
    return allUsers.filter((u) => u.id !== targetUser.id
      && ((u.name || "").toLowerCase().includes(query) || (u.email || "").toLowerCase().includes(query) || (u.division || "").toLowerCase().includes(query)));
  }, [allUsers, q, targetUser.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="audit-access-dialog">
      <div className="bg-white border border-slate-200 shadow-2xl w-full max-w-2xl mt-8">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <p className="overline text-[9px] text-slate-500 flex items-center gap-1"><FileClock size={11} /> Akses Audit Log</p>
          <h2 className="font-display text-lg text-[#0B1D3A]">{targetUser.name}</h2>
          <p className="text-[10px] text-slate-500">{targetUser.email} · {targetUser.division} Lv{targetUser.level}</p>
        </div>
        <div className="p-4 space-y-4 text-[11px]">
          <div>
            <div className="overline text-[9px] text-slate-500 tracking-widest mb-1.5">Tipe Cakupan</div>
            <div className="flex flex-wrap gap-1" data-testid="audit-scope-type">
              {[["all", "Semua User"], ["division", "Divisi Tertentu"], ["user", "User Tertentu"]].map(([k, label]) => (
                <button key={k} type="button" onClick={() => setScopeType(k)} data-testid={`audit-scope-${k}`}
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold border ${scopeType === k ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:text-[#0B1D3A]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {scopeType === "division" && (
            <div data-testid="audit-scope-div-picker">
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1.5">Pilih Divisi</div>
              <div className="flex flex-wrap gap-1">
                {DIVISIONS.map((d) => (
                  <button key={d} type="button" onClick={() => toggleDiv(d)}
                    className={`px-2.5 py-1 text-[10px] font-semibold border ${divisions.includes(d) ? "bg-violet-100 text-violet-800 border-violet-400" : "bg-white text-slate-600 border-slate-300"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {scopeType === "user" && (
            <div data-testid="audit-scope-user-picker">
              <div className="overline text-[9px] text-slate-500 tracking-widest mb-1.5">Pilih User ({userIds.length} dipilih)</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari user…"
                className="w-full h-8 border border-slate-300 px-2 text-[11px] bg-white mb-1.5" />
              <div className="border border-slate-200 max-h-56 overflow-y-auto">
                {filteredUsers.slice(0, 100).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-2 py-1 border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={userIds.includes(u.id)} onChange={() => toggleUser(u.id)} />
                    <span className="flex-1 text-[11px] text-slate-700">{u.name} <span className="text-slate-400">· {u.division} Lv{u.level}</span></span>
                  </label>
                ))}
                {filteredUsers.length === 0 && <div className="text-center py-4 text-slate-400 italic text-[10px]">Tidak ada user cocok.</div>}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          {data.scope_type && data.scope_type !== "all" ? (
            <button type="button" onClick={revoke} disabled={busy} data-testid="audit-scope-revoke"
              className="px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">Cabut Override</button>
          ) : (data.grant?.scope_type === "all" ? (
            <button type="button" onClick={revoke} disabled={busy} data-testid="audit-scope-revoke"
              className="px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50 text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">Cabut Override</button>
          ) : <span />)}
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 text-[10px] uppercase tracking-wider font-semibold"><X size={11} className="inline" /> Batal</button>
            <button type="button" onClick={save} disabled={busy} data-testid="audit-scope-save"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold disabled:opacity-50">
              <Save size={11} /> {busy ? "Menyimpan…" : "Simpan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
